import { Link } from 'react-router-dom';
import SectionHeading from '@/components/base/SectionHeading';
import Reveal from '@/components/feature/Reveal';

const RULES = [
  {
    id: 'stop',
    title: 'A hard stop on every trade',
    text: 'Each position ships with a stop-loss and a take-profit attached the moment it opens. No trade is ever left naked, and a losing position is never given more room.',
  },
  {
    id: 'size',
    title: 'Size is fixed, never doubled',
    text: 'Risk per position is a fixed fraction of equity. No martingale, no grid, no averaging down — the fastest route from one bad week to a blown account.',
  },
  {
    id: 'drawdown',
    title: 'Drawdown is published, not smoothed',
    text: 'Red months and deep pullbacks are part of trading gold. We show the account exactly as it ran — including the months that hurt — instead of a flattering curve.',
  },
];

export default function RiskDiscipline() {
  return (
    <section className="py-16 md:py-24 bg-background-100">
      <div className="w-full px-4 md:px-8">
        <div className="max-w-shell mx-auto">
          <Reveal>
            <SectionHeading
              eyebrow="Risk, honestly"
              title="The numbers change. The rules don't."
              subtitle="This account runs live, so drawdown moves and months vary — nobody can promise the next quarter. What stays fixed is exactly how risk is handled on every single trade."
            />
          </Reveal>

          <div className="mt-10 md:mt-12 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
            {RULES.map((rule, index) => (
              <Reveal key={rule.id} delay={index * 90}>
                <div className="h-full rounded-lg border border-background-200 bg-background-50 p-6 md:p-7 transition-colors duration-200 hover:border-accent-300">
                  <span className="block w-10 h-1 rounded-full bg-accent-500" aria-hidden="true"></span>
                  <h3 className="mt-5 font-heading text-base md:text-lg font-semibold text-foreground-950">
                    {rule.title}
                  </h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-foreground-600">{rule.text}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={120}>
            <div className="mt-6 rounded-lg bg-primary-950 p-6 md:p-8 grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-6 lg:gap-10 items-center">
              <div>
                <div className="flex items-center gap-2.5">
                  <span className="w-8 h-8 flex items-center justify-center rounded-md bg-accent-500 text-primary-950">
                    <i className="ri-error-warning-line text-base" aria-hidden="true"></i>
                  </span>
                  <h3 className="font-heading text-lg md:text-xl font-bold text-background-50">
                    We don’t project the future from the past.
                  </h3>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-background-200/85 max-w-2xl">
                  Past performance is a record, not a forecast. The EA trades live and these numbers move
                  with it — so treat everything on this site as what happened, never a promise of what
                  comes next.
                </p>
              </div>
              <Link
                to="/performance"
                className="inline-flex items-center gap-2 rounded-md border border-background-50/20 px-5 py-3 text-sm font-semibold text-background-50 hover:bg-background-50/10 transition-colors whitespace-nowrap cursor-pointer"
              >
                See the live record
                <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </Link>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}